/**
 * Mission 14's rail yard: the diesel shunter and the tank wagons, each standing on a length of its
 * own track (ballast, sleepers, rails) exactly as long as its collider - so wagons placed end to end
 * make a siding, and no rail prop is needed. Both run along z; at yaw 0 the engine's nose is SOUTH.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { snowPatch, tubeZ, wheel } from './parts';

/** Top of the rail above the model's y = 0, and half the gauge: what every wheel stands on. */
export const RAIL_TOP = 0.4;
export const HALF_GAUGE = 0.72;

/** Ballast, sleepers and two rails along z, `length` metres, centred on the origin. */
export function trackBed(mb: MeshBuilder, p: Palette, length: number): void {
  mb.box(2.9, 0.2, length, { all: p.color('rock.lo'), top: p.color('rock.mid') });
  const sleepers = Math.round(length / 1.25);
  for (let i = 0; i < sleepers; i++) {
    const z = -length / 2 + (length * (i + 0.5)) / sleepers;
    mb.box(2.5, 0.1, 0.26, p.color('timber.lo'), place(0, 0.18, z));
  }
  // The railhead is polished bright: two thin light lines are what say "railway" from above.
  const rail = { all: p.color('metal.lo'), top: p.color('metal.hi') };
  for (const sx of [-1, 1]) mb.box(0.1, 0.14, length, rail, place(sx * HALF_GAUGE, RAIL_TOP - 0.14, 0));
}

/**
 * ROUND4 PLACEHOLDER (VEH): a free-standing run of track for the `rail` prop kind, drawn to `length` along
 * X like every other run and turned by the prop's `yaw`. It is `trackBed` with the axes swapped, so that
 * mission 14's train has rails under it once it leaves the yard. FIELD to replace with the proper model.
 */
export function railRun(p: Palette, _rng: Pcg32, length = 8): MeshBuilder {
  const mb = new MeshBuilder();
  // A hair below the beds the parked stock carries, so a siding laid over standing wagons does not z-fight.
  const y = -0.03;
  mb.box(length, 0.2, 2.9, { all: p.color('rock.lo'), top: p.color('rock.mid') }, place(0, y, 0));
  const sleepers = Math.max(1, Math.round(length / 1.25));
  for (let i = 0; i < sleepers; i++) {
    const x = -length / 2 + (length * (i + 0.5)) / sleepers;
    mb.box(0.26, 0.1, 2.5, p.color('timber.lo'), place(x, y + 0.18, 0));
  }
  const rail = { all: p.color('metal.lo'), top: p.color('metal.hi') };
  for (const sz of [-1, 1]) mb.box(length, 0.14, 0.1, rail, place(0, y + RAIL_TOP - 0.14, sz * HALF_GAUGE));
  return mb;
}

/** Red buffer beam and a pair of buffers on the end at `z` (`out` is +1 for the south end, -1 for the north). */
function bufferBeam(mb: MeshBuilder, p: Palette, z: number, out: 1 | -1, y: number): void {
  mb.box(2.6, 0.45, 0.2, p.color('enemyAccent.0'), place(0, y, z));
  for (const sx of [-1, 1]) {
    const z0 = out > 0 ? z + 0.1 : z - 0.45;
    tubeZ(mb, sx * 0.85, y + 0.22, z0, z0 + 0.35, 0.16, 0.16, 5, p.color('metal.lo'), p.color('metal.mid'));
  }
}

/**
 * Diesel shunter: long bonnet to the south with its grille and headlamp facing the camera, cab
 * behind it, red buffer beams and red wheel centres. Painted in the roof ramp (slate, or brown in
 * the desert) - black would read as already burnt, which is what the wreck looks like.
 */
export const locomotive: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  trackBed(mb, p, 10);
  shunter(mb, p);
  return mb;
};

/**
 * How far the shunter stands above its railhead (wheel centre `RAIL_TOP + 0.45`, radius 0.52): what
 * the MOVING train drops by, because nothing lays track under a vehicle that is driving a route.
 */
export const RAIL_WHEEL_DROP = RAIL_TOP + 0.45 - 0.52;

/**
 * The train under way (ROUND4 §1, `EnemyKind` `railEngine`): the same shunter with no track beneath
 * it, dropped so its wheels stand on the ground instead of on a railhead that is not there.
 */
export const railEngine: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.with(place(0, -RAIL_WHEEL_DROP, 0), () => shunter(mb, p));
  return mb;
};

/** Its headlamp alone, a shade proud of the body's: drawn self-lit after dark by `world/Machines`. */
export const railEngineLamp: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.34, 0.28, 0.05, p.color('fire.3'), place(0, 1.3 + 1.23 - RAIL_WHEEL_DROP, 4.11));
  return mb;
};

/** The shunter itself, in the frame of the track it stands on. */
function shunter(mb: MeshBuilder, p: Palette): void {
  const paint: BoxColors = { all: p.color('roof.1'), left: p.color('roof.0'), right: p.color('roof.0') };
  const deck = 1.3;

  // Running gear: three coupled axles, a rod along each side, under a black frame.
  for (const sx of [-1, 1]) {
    for (const z of [-2.0, 0, 2.0]) wheel(mb, p, [sx * HALF_GAUGE, RAIL_TOP + 0.45, z], 0.52, 0.16, 6, 'enemyAccent.0');
    mb.box(0.06, 0.1, 4.3, p.color('metal.mid'), place(sx * (HALF_GAUGE + 0.12), RAIL_TOP + 0.3, 0));
  }
  mb.box(2.7, 0.28, 8.9, { all: p.color('ink.1'), top: p.color('metal.lo') }, place(0, deck - 0.28, 0));
  bufferBeam(mb, p, 4.45, 1, 0.8);
  bufferBeam(mb, p, -4.45, -1, 0.8);

  // Bonnet, with a yellow band round its foot; cab; short rear bonnet.
  mb.box(2.0, 1.5, 5.0, paint, place(0, deck, 1.55));
  mb.box(2.04, 0.2, 5.04, p.color('fire.2'), place(0, deck + 0.05, 1.55));
  mb.box(2.6, 2.1, 2.2, paint, place(0, deck, -2.05));
  mb.box(2.0, 1.15, 1.25, paint, place(0, deck, -3.75));
  mb.box(2.9, 0.15, 2.6, { all: p.color('metal.lo'), top: p.color('metal.mid') }, place(0, deck + 2.1, -2.05));
  snowPatch(mb, p, -0.2, deck + 2.25, -2.1, 1.9, 1.6);
  snowPatch(mb, p, 0.15, deck + 1.5, 2.6, 1.3, 1.7);
  snowPatch(mb, p, -0.2, deck + 1.5, 0.5, 1.1, 1.0);

  // Glazing: the strip of cab that shows over the bonnet, and a window each side.
  for (const sx of [-1, 1]) {
    mb.box(0.8, 0.42, 0.06, p.color('ink.1'), place(sx * 0.75, deck + 1.58, -0.94));
    mb.box(0.06, 0.6, 1.3, p.color('ink.1'), place(sx * 1.3, deck + 1.3, -2.05));
  }
  // Radiator grille and headlamp on the nose; exhaust stack on the bonnet.
  mb.box(1.5, 1.0, 0.06, p.color('ink.0'), place(0, deck + 0.2, 4.06));
  for (const y of [0.5, 0.85]) mb.box(1.5, 0.08, 0.1, p.color('metal.mid'), place(0, deck + y, 4.07));
  mb.box(0.32, 0.26, 0.1, p.color('fire.3'), place(0, deck + 1.24, 4.06));
  mb.cylinder(0.17, 0.14, 0.5, 6, p.color('ink.1'), place(0, deck + 1.5, 3.2), p.color('ink.0'));
}

/**
 * Two-axle tank wagon: a bright steel tank with two red hazard hoops and a filler dome, the same
 * metal-and-red as the fuel tank it fills. The yellow plate on the south end says what is in it.
 */
export const fuelWagon: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  trackBed(mb, p, 8);
  const deck = 1.15;
  for (const sx of [-1, 1]) {
    for (const z of [-2.2, 2.2]) wheel(mb, p, [sx * HALF_GAUGE, RAIL_TOP + 0.39, z], 0.45, 0.16, 6);
  }
  mb.box(2.5, 0.22, 7.2, { all: p.color('ink.1'), top: p.color('metal.lo') }, place(0, deck - 0.22, 0));
  bufferBeam(mb, p, 3.5, 1, 0.75);
  bufferBeam(mb, p, -3.5, -1, 0.75);
  for (const z of [-2.0, 2.0]) mb.box(1.9, 0.4, 0.45, p.color('metal.lo'), place(0, deck, z));

  const r = 0.88;
  const axis = deck + 0.25 + r;
  tubeZ(mb, 0, axis, -3.3, 3.3, r, r, 10, p.color('metal.mid'), p.color('metal.lo'));
  for (const z of [-1.9, 1.6]) tubeZ(mb, 0, axis, z, z + 0.3, r + 0.04, r + 0.04, 10, p.color('enemyAccent.0'));
  mb.box(0.62, 0.62, 0.05, p.color('fire.2'), place(0, axis - 0.31, 3.31));
  // Filler dome and the plank walkway along the crown.
  mb.box(0.5, 0.06, 5.2, p.color('timber.mid'), place(0.0, axis + r - 0.03, 0));
  mb.cylinder(0.36, 0.3, 0.32, 6, p.color('metal.lo'), place(0, axis + r - 0.02, -0.2), p.color('metal.mid'));
  snowPatch(mb, p, 0, axis + r + 0.03, 1.9, 0.44, 1.6);
  snowPatch(mb, p, 0, axis + r + 0.03, -1.9, 0.44, 1.2);
  return mb;
};
